import { useCallback, useEffect, useMemo, useState } from 'react';

import { useViewport, VIEWPORT_TYPE } from 'src/hooks/useViewport';

import { newsAdapter } from '../adapters';
import type { ServerNews } from '../types';

type UseNewsDataProps = {
  data: ServerNews[];
};

const INITIAL_LIMIT_DESKTOP = 12;
const INITIAL_LIMIT_TABLET = 8;
const INITIAL_LIMIT_MOBILE = 4;

const LOAD_NEWS_COUNT_DESKTOP = 6;
const LOAD_NEWS_COUNT_TABLET = 4;
const LOAD_NEWS_COUNT_MOBILE = 4;

const getInitialLimitByViewport = (viewport: VIEWPORT_TYPE) => {
  switch (viewport) {
    case VIEWPORT_TYPE.Desktop:
      return INITIAL_LIMIT_DESKTOP;
    case VIEWPORT_TYPE.Tablet:
      return INITIAL_LIMIT_TABLET;
    case VIEWPORT_TYPE.Mobile:
    default:
      return INITIAL_LIMIT_MOBILE;
  }
};

const getCategoryFromLabel = (label: string) => label.trim().toLowerCase();

export const useNewsData = ({ data }: UseNewsDataProps) => {
  const { viewport } = useViewport();

  const [categoriesList, setCategoriesList] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>();
  const [limit, setLimit] = useState(0);

  const newsList = useMemo(() => {
    const filteredData = selectedCategory
      ? data.filter((newsItem) => getCategoryFromLabel(newsItem.label) === selectedCategory)
      : data;

    return filteredData.map(newsAdapter.toClient).sort((_, b) => {
      return b.isPinned ? 1 : -1;
    });
  }, [data, selectedCategory, limit]);

  const resetLimit = useCallback(() => {
    if (viewport) {
      setLimit(getInitialLimitByViewport(viewport));
    }
  }, [viewport]);

  const loadMore = useCallback(() => {
    setLimit((prevState) => {
      switch (viewport) {
        case VIEWPORT_TYPE.Desktop:
          return prevState + LOAD_NEWS_COUNT_DESKTOP;
        case VIEWPORT_TYPE.Tablet:
          return prevState + LOAD_NEWS_COUNT_TABLET;
        case VIEWPORT_TYPE.Mobile:
        default:
          return prevState + LOAD_NEWS_COUNT_MOBILE;
      }
    });
  }, [viewport]);

  useEffect(() => {
    const uniqueCategories = Array.from(
      new Set(data.map((newsItem) => getCategoryFromLabel(newsItem.label)))
    ).filter(Boolean);
    setCategoriesList(uniqueCategories);
  }, [data]);

  useEffect(() => {
    if (limit > 0 || viewport === undefined) {
      return;
    }

    setLimit(getInitialLimitByViewport(viewport));
  }, [viewport, limit]);

  return {
    newsList,
    categoriesList,
    selectedCategory,
    setSelectedCategory,
    limit,
    setLimit,
    resetLimit,
    loadMore,
  };
};
